import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { LangProvider } from "./lib/i18n";
import { AuthProvider, useAuth } from "./lib/auth";
import Layout from "./components/Layout";
import { Loading } from "./components/common";
import Login from "./pages/Login";
import ForcePasswordChange from "./pages/ForcePasswordChange";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import BlockDevice from "./pages/BlockDevice";
import AcceptInvite from "./pages/AcceptInvite";
import Companies from "./pages/Companies";
import Profile from "./pages/Profile";
import PlatformAdmin from "./pages/PlatformAdmin";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Pipeline from "./pages/Pipeline";
import CalendarPage from "./pages/CalendarPage";
import Estimates from "./pages/Estimates";
import EstimateEditor from "./pages/EstimateEditor";
import Contracts from "./pages/Contracts";
import ContractDetail from "./pages/ContractDetail";
import BillingDoc from "./pages/BillingDoc";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import FollowUps from "./pages/FollowUps";
import Warranties from "./pages/Warranties";
import Assistant from "./pages/Assistant";
import MaterialCalculator from "./pages/MaterialCalculator";
import Inventory from "./pages/Inventory";
import Team from "./pages/Team";
import Reports from "./pages/Reports";
import SettingsPage from "./pages/SettingsPage";
import ActivityLog from "./pages/ActivityLog";
import More from "./pages/More";
import Notifications from "./pages/Notifications";
import PublicSign from "./pages/PublicSign";
import PublicReview from "./pages/PublicReview";
import PublicPortal from "./pages/PublicPortal";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  return children;
}

const Staff = ({ children }) => {
  const { isStaff } = useAuth();
  return isStaff ? children : <Navigate to="/" replace />;
};

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/recover-owner/:token" element={<ResetPassword endpoint="/auth/recover-owner" />} />
            <Route path="/security/block/:token" element={<BlockDevice />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/sign/:token" element={<PublicSign />} />
            <Route path="/review/:token" element={<PublicReview />} />
            <Route path="/portal/:token" element={<PublicPortal />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="customers" element={<Staff><Customers /></Staff>} />
              <Route path="customers/:id" element={<Staff><CustomerDetail /></Staff>} />
              <Route path="pipeline" element={<Staff><Pipeline /></Staff>} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="estimates" element={<Staff><Estimates /></Staff>} />
              <Route path="estimates/:id" element={<Staff><EstimateEditor /></Staff>} />
              <Route path="contracts" element={<Staff><Contracts /></Staff>} />
              <Route path="contracts/:id" element={<Staff><ContractDetail /></Staff>} />
              <Route path="billing/:id" element={<Staff><BillingDoc /></Staff>} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
              <Route path="followups" element={<Staff><FollowUps /></Staff>} />
              <Route path="warranties" element={<Staff><Warranties /></Staff>} />
              <Route path="assistant" element={<Staff><Assistant /></Staff>} />
              <Route path="calculator" element={<MaterialCalculator />} />
              <Route path="inventory" element={<Staff><Inventory /></Staff>} />
              <Route path="team" element={<Staff><Team /></Staff>} />
              <Route path="reports" element={<Staff><Reports /></Staff>} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="companies" element={<Companies />} />
              <Route path="platform" element={<PlatformAdmin />} />
              <Route path="profile" element={<Profile />} />
              <Route path="activity" element={<Staff><ActivityLog /></Staff>} />
              <Route path="more" element={<More />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
